import { forwardRef, type SelectHTMLAttributes } from 'react';

import { cn } from './cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options: SelectOption[];
  placeholder?: string;
  hasError?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, hasError = false, className, ...rest },
  ref,
) {
  return (
    // The wrapper exists for ONE reason: a native <select> cannot contain the
    // custom chevron, and `appearance-none` removes the browser's. Layout
    // classes callers pass (heights, margins, widths) still land on the
    // <select>, so the wrapper must take its size FROM the <select>, never
    // impose one: with no width of its own it fills a block or grid cell like
    // any div (the `w-full` <select> fills it back), and shrinks to the
    // <select> inside a flex row. It carried `w-full` once, which in a toolbar
    // row swallowed the whole line — a `!w-auto` filter sat at the left with
    // its chevron pinned to the far right edge and the search box wrapped
    // underneath it.
    <div className="relative">
      <select
        ref={ref}
        aria-invalid={hasError || undefined}
        className={cn(
          'h-10 w-full appearance-none bg-bg text-foreground',
          'border rounded-md pl-3 pr-9 text-sm transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          hasError ? 'border-danger focus-visible:ring-danger' : 'border-border',
          className,
        )}
        {...rest}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      {/* Decorative — the control itself announces expanded/collapsed. */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/50"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M4 6l4 4 4-4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
});
